import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { minutesToTime } from '../../types/working-hours';

interface TimePickerProps {
  value: number;
  onChange: (minutes: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

function generateOptions(step: number): number[] {
  const options: number[] = [];
  for (let m = 0; m < 1440; m += step) {
    options.push(m);
  }
  return options;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  min,
  max,
  step = 15,
  className,
  disabled,
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const options = React.useMemo(() => generateOptions(step), [step]);

  const selectedIndex = options.indexOf(value);

  const isOptionDisabled = useCallback(
    (minutes: number) => {
      if (min !== undefined && minutes < min) return true;
      if (max !== undefined && minutes > max) return true;
      return false;
    },
    [min, max]
  );

  const positionDropdown = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width < 100 ? 100 : rect.width,
      zIndex: 9999,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top }
        : { top: rect.bottom + 2 }),
    });
  }, []);

  const open = useCallback(() => {
    if (disabled) return;
    positionDropdown();
    setIsOpen(true);
    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [disabled, positionDropdown, selectedIndex]);

  const close = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  }, []);

  // Scroll to selected/focused option
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const target = focusedIndex >= 0 ? focusedIndex : selectedIndex;
    if (target < 0) return;
    const item = listRef.current.children[target] as HTMLElement | undefined;
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, focusedIndex, selectedIndex]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      )
        return;
      close();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, close]);

  // Close on ancestor scroll (prevents misalignment in side panels)
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      close();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, close]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) open();
    }
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && !isOptionDisabled(options[focusedIndex])) {
          onChange(options[focusedIndex]);
          close();
        }
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(options.length - 1);
        break;
    }
  };

  const listboxId = React.useId();

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleTriggerKeyDown}
        className={
          className ||
          'flex items-center gap-1 px-2 sm:px-3 py-2 bg-white border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed'
        }
      >
        <span>{minutesToTime(value)}</span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
      </button>

      {isOpen && (
        <div ref={dropdownRef} style={dropdownStyle}>
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel || 'Select time'}
            tabIndex={0}
            onKeyDown={handleListKeyDown}
            className="max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1 focus:outline-none"
            style={{ scrollBehavior: 'auto' }}
          >
            {options.map((minutes, index) => {
              const optDisabled = isOptionDisabled(minutes);
              const isSelected = minutes === value;
              const isFocused = index === focusedIndex;

              return (
                <li
                  key={minutes}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={optDisabled}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!optDisabled) {
                      onChange(minutes);
                      close();
                    }
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  className={`
                    px-3 py-1.5 text-sm cursor-pointer select-none
                    ${isSelected ? 'bg-purple-600 text-white' : ''}
                    ${!isSelected && isFocused ? 'bg-purple-50' : ''}
                    ${optDisabled ? 'text-gray-300 cursor-not-allowed' : ''}
                    ${!isSelected && !isFocused && !optDisabled ? 'text-gray-700' : ''}
                  `}
                >
                  {minutesToTime(minutes)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

export default TimePicker;
